// CPQ Operation runtime. Provides the JS helpers that NetSuite CPQ scripts
// in operations.json expect (getValue, setValue, getQA, getAnswerCode,
// getAnswerVariable, getQTable, setAnswer, trySet, ...).
//
// A runtime holds a *mutable working copy* of answer state, table rows, and
// row selections. Scripts read & write through it; after all ops run, the
// caller commits the working copy back to React state.

import type {
  AnswerState,
  AnswerValue,
  Group,
  Material,
  Question,
  QTables,
  Scratchpad,
  SelectedRowState,
  Tab,
  TableRow,
  TableState,
} from "../types";
import { evalRule, isHidden } from "./rule";

// Genuine user-input fields that must KEEP their value in the encoded mapping even
// when the group/tab that renders them is hidden. Declared per configurator (see
// src/products/): in Natural Draft Burners that is CELL_OD (flame cell OD), sized for
// every non-FAH burner and defining the item whether or not its Flame Cells group is
// shown, so nulling it would wrongly clear a valid attribute. Every OTHER
// group/tab-hidden mapped field goes to its none token (see userFieldHidden).
import { getProfile, NEUTRAL_PROFILE } from "../products";

export type RuntimeOptions = {
  answers: AnswerState;
  tables: TableState;
  selectedRows: SelectedRowState;
  questions: Question[];
  qtables: QTables;
  // Tabs (with nested _groups) — used only by mapping mode to null a field whose
  // enclosing group/tab is hidden. Optional: callers that don't pass it simply
  // skip the group/tab hide check (own-rule behavior, as before).
  tabs?: Tab[];
  materials?: Material[];
  scratchpad?: Scratchpad;
  // Which configurator is running (a products.json slug). Used to look up that
  // product's mapping hide-exempt fields. Optional so a caller that does not encode
  // a mapping (and every existing test) needs no change; omitted means NO exemptions,
  // never another product's. CELL_OD exists in more than one export, so inheriting
  // Natural Draft's exemption would be a silent cross-product decision.
  product?: string;
};

export type Runtime = {
  // After ops run, these contain the new state to commit.
  answers: AnswerState;
  tables: TableState;
  selectedRows: SelectedRowState;
  scratchpad: Scratchpad;
  // Toggle "mapping mode" — while on, hidden user-editable fields read as null
  // (used only while the MAPPING operation runs).
  setMappingMode: (on: boolean) => void;
  // Functions exposed to operation scripts.
  api: RuntimeApi;
};

type GetDataChain = { done: (cb: (res: unknown) => void) => GetDataChain };

export type RuntimeApi = {
  getValue: (qCode: string, aCode: string) => AnswerValue | undefined;
  setValue: (qCode: string, aCode: string, val: AnswerValue) => void;
  getQA: (code: string) => AnswerValue | undefined;
  getAnswerCode: (qCode: string) => string | undefined;
  setAnswer: (qCode: string, valueOrCode: AnswerValue) => void;
  getAnswerVariable: (qaPath: string, varName: string) => string | undefined;
  getAnswerLabel: (qaPath: string) => string | undefined;
  // The answer record's `name` field, which CPQ keeps SEPARATE from its label:
  // NDB's Standard hood is name "Hood Standard" but label "<p>Standard</p>". The
  // Flame Cell scripts build the ID key, display name, description and sales
  // description out of names, so they are load-bearing there, not cosmetic.
  // Accepts BOTH forms the scripts use: "QUESTION" (the selected answer) and
  // "QUESTION/ANSWER" (that specific answer).
  getAnswerName: (qOrQaPath: string) => string | undefined;
  // A body field on the launching sales order. NOT MODELLED YET - see the
  // implementation for why it returns "" rather than undefined.
  getOrderField: (fieldId: string) => string;
  // Strips a parenthesised suffix off a nozzle label: "Foo (bar)" -> "Foo".
  //
  // Pilot needs this because its export DECLARES the function in one operation and
  // CALLS it from another. NetSuite evidently evaluates every operation source in one
  // shared scope and hoists the declarations, so that works there; cpq2 wraps each
  // operation in its own IIFE, so for an ASSEMBLY_FULL configuration the call was a
  // ReferenceError and SALES_DESCRIPTION - the line right after it - never got set.
  //
  // Supplied as an API helper rather than by changing how operations are scoped,
  // because that would alter evaluation for Natural Draft's 55 operations too. The
  // operation that declares its own copy still wins: a function declaration cleanly
  // shadows the wrapper's parameter of the same name (see compileOperation).
  cleanNozzleLabel: (label: string) => string;
  getInputValue: (qCode: string, aCode: string) => AnswerValue | undefined;
  getSelectInternalId: (qCode: string) => string | undefined;
  getQTable: (code: string) => TableHandle;
  getTable: (code: string) => TableHandle;
  getQTableCell: (
    tableCode: string,
    colCode: string,
    row1Based?: number
  ) => AnswerValue | undefined;
  trySet: (qCode: string, val: AnswerValue) => void;
  reset: (qCode: string, aCode: string) => void;
  scratchpad: Scratchpad;
  getOrderData: (...args: unknown[]) => unknown;
  getRuleset: (name: string) => unknown[];
  getLabel: (...args: unknown[]) => unknown;
  recalcRulesets: (...args: unknown[]) => unknown;
  createRecord: (...args: unknown[]) => unknown;
  updateRecordFields: (...args: unknown[]) => unknown;
  getData: (...args: unknown[]) => GetDataChain;
};

export type TableHandle = {
  clear: () => void;
  setRowCount: (n: number) => void;
  set: (colCode: string, row1Based: number, val: AnswerValue) => void;
  // get(): returns the whole rows array (used by scripts that scan the table).
  // get(col, row): returns a single cell.
  get: (
    colCode?: string,
    row1Based?: number
  ) => AnswerValue | TableRow[] | undefined;
  // Async-compatible (await x is fine on a non-Promise). Scripts use this.
  loadContent: () => TableRow[];
};

export function createRuntime(opts: RuntimeOptions): Runtime {
  // Deep-ish copies — we mutate during the run, then commit.
  const answers: AnswerState = { ...opts.answers };
  const tables: TableState = {};
  for (const [k, rows] of Object.entries(opts.tables)) {
    tables[k] = rows.map((r) => ({ ...r }));
  }
  const selectedRows: SelectedRowState = { ...opts.selectedRows };
  // Scratchpad is shared across operations and persists across runs.
  // Callers pass in the live ref so prevHousing-style transition detectors
  // work between Calculate/select-row events.
  const scratchpad: Scratchpad = opts.scratchpad ?? {};
  const materials: Material[] = opts.materials ?? [];
  // No product named -> NEUTRAL_PROFILE -> no exemptions. Deliberately not a
  // default product: an unnamed caller must not inherit Natural Draft's.
  const mappingHideExempt = (opts.product ? getProfile(opts.product) : NEUTRAL_PROFILE)
    .mappingHideExemptCodes;

  const questionsByCode = new Map<string, Question>();
  for (const q of opts.questions) {
    questionsByCode.set(q.custrecord_cpqc_question_code, q);
  }

  // Group/tab lookup (by numeric-string id) for the mapping-mode group/tab hide
  // check in userFieldHidden. A question references its group/tab by id
  // (custrecord_cpqc_question_group / _tab), and groups are nested under tabs.
  const tabsById = new Map<string, Tab>();
  const groupsById = new Map<string, Group>();
  for (const t of opts.tabs ?? []) {
    tabsById.set(t.id, t);
    for (const g of t._groups ?? []) groupsById.set(g.id, g);
  }

  const customVarCache = new Map<string, Record<string, string>>();

  // Mapping mode: while on, reads of a genuine *user-input* question that is
  // currently hidden — by its own hide rule OR because its enclosing group/tab
  // is hidden — return null/undefined, so the encoded mapping treats it as null.
  // The value stays in `answers` (held) so it returns when the field is shown
  // again. This drops stale/inapplicable inputs (e.g. 38_TUBING when there's no
  // pilot, HINGE_SIZE on an O-Type, Manway options on a non-Box housing) so they
  // don't leak into the item. See MAPPING_GROUP_TAB_HIDE_EXEMPT for the one
  // field (CELL_OD) kept despite a hidden group.
  //
  // EXCLUDED: type-7 questions are COMPUTED/derived (single answer slot holding a
  // calculated value — DESIGN, FIRETUBE_DIAMETER1, DIMENSIONS, MIXER_MAPPING,
  // CELL_QUANTITYV, BASECELLSIZE, …). They're "hidden" only because they're
  // display-only, but their value always reflects the current config and MUST be
  // encoded. Nulling them here made those item attributes decode to 00 and get
  // CLEARED by the NetSuite mapping decoder (item 563980 lost DESIGN/FIRETUBE/
  // DIMENSIONS/MIXER/CELL_QUANTITY/BASECELLSIZE). So mappingMode only touches
  // real user-input types (4/11), never computed (7). Always-hidden questions
  // (custrecord_cpqc_question_hide) are unaffected regardless.
  let mappingMode = false;
  function userFieldHidden(qCode: string): boolean {
    if (!mappingMode) return false;
    const q = questionsByCode.get(qCode);
    if (!q) return false;
    const t = q.custrecord_cpqc_question_type;
    if (t !== "4" && t !== "11") return false; // genuine user inputs only (7 = computed: keep)
    const alwaysHidden = q.custrecord_cpqc_question_hide as unknown;
    if (alwaysHidden === true || alwaysHidden === "T") return false; // always-hidden: excluded
    // (a) The field's OWN hide rule fires.
    const rule = q.custrecord_cpqc_question_p_hide_rule as string | undefined;
    if (rule && evalRule(rule, answers)) return true;
    // (b) The field is invisible because its enclosing GROUP or TAB is hidden.
    // A stale value left inside a hidden section (e.g. Manway options on a
    // non-Box housing, or the main-burner fields when there are 0 burners / an
    // FAH) must encode as its none token rather than leaking the old value.
    // Uses the same isHidden() the UI uses to hide the group/tab, so mapping
    // visibility matches on-screen visibility. Exempt fields (CELL_OD) hold a
    // config-defining value even while their group is hidden, so they keep it.
    if (!mappingHideExempt.has(qCode)) {
      const g = groupsById.get(q.custrecord_cpqc_question_group as string);
      if (
        g &&
        isHidden(
          {
            hideRule: g.custrecord_cpqc_group_hide_rule,
            parsedHideRule: g.custrecord_cpqc_group_parsed_hide_rule,
          },
          answers
        )
      )
        return true;
      const tb = tabsById.get(q.custrecord_cpqc_question_tab as string);
      if (
        tb &&
        isHidden(
          {
            hideRule: tb.custrecord_cpqc_tab_hide_rule,
            parsedHideRule: tb.custrecord_cpqc_tab_parsed_hide_rule,
          },
          answers
        )
      )
        return true;
    }
    return false;
  }

  function selectedAnswerCode(qCode: string): string | undefined {
    const q = questionsByCode.get(qCode);
    if (!q) return undefined;
    for (const a of q._answers) {
      const aCode = a.custrecord_cpqc_answer_code;
      if (answers[`${qCode}/${aCode}`] === true) return aCode;
    }
    return undefined;
  }

  function getCustomVars(qaPath: string): Record<string, string> {
    const cached = customVarCache.get(qaPath);
    if (cached) return cached;
    const [qCode, aCode] = qaPath.split("/");
    const q = questionsByCode.get(qCode);
    const ans = q?._answers.find(
      (a) => a.custrecord_cpqc_answer_code === aCode
    );
    const raw = (ans?.custrecord_cpqc_answer_custom_vars as string) ?? "";
    let parsed: Record<string, string> = {};
    if (raw) {
      try {
        parsed = JSON.parse(raw);
      } catch {
        // ignore
      }
    }
    customVarCache.set(qaPath, parsed);
    return parsed;
  }

  function ensureTable(code: string): TableRow[] {
    if (!tables[code]) tables[code] = [];
    return tables[code];
  }

  const api: RuntimeApi = {
    getValue(qCode, aCode) {
      if (userFieldHidden(qCode)) return null;
      return answers[`${qCode}/${aCode}`];
    },
    setValue(qCode, aCode, val) {
      answers[`${qCode}/${aCode}`] = val;
    },
    getQA(code) {
      if (userFieldHidden(code)) return null;
      return answers[`${code}/${code}`];
    },
    getAnswerCode(qCode) {
      if (userFieldHidden(qCode)) return undefined;
      return selectedAnswerCode(qCode);
    },
    setAnswer(qCode, valueOrCode) {
      // CPQ scripts call setAnswer two ways:
      //   - Single-answer question (free-text / computed): write VALUE
      //     into that question's one answer slot.
      //   - Multi-answer picklist: arg is the answer code to "pick"; set
      //     it true and others null.
      const q = questionsByCode.get(qCode);
      if (!q || q._answers.length === 0) {
        answers[`${qCode}/${qCode}`] = valueOrCode;
        return;
      }
      if (q._answers.length === 1) {
        const aCode = q._answers[0].custrecord_cpqc_answer_code;
        answers[`${qCode}/${aCode}`] = valueOrCode;
        return;
      }
      for (const a of q._answers) {
        const aCode = a.custrecord_cpqc_answer_code;
        answers[`${qCode}/${aCode}`] = aCode === valueOrCode ? true : null;
      }
    },
    getAnswerVariable(qaPath, varName) {
      const vars = getCustomVars(qaPath);
      return vars[varName];
    },
    getQTable(code) {
      // type=11 questions are stored two ways simultaneously:
      //   tables[code] = [{COL: val, ...}, ...]               (for our table widget)
      //   answers[`${code}/${COL}_${row}`] = val              (so CPQ scripts that
      //                                                        read via getValue work)
      // Both stay in sync so any script (or React component) can read either way.
      ensureTable(code);

      const writeCell = (
        colCode: string,
        row1Based: number,
        val: AnswerValue
      ) => {
        const rows = ensureTable(code);
        const idx = row1Based - 1;
        while (rows.length <= idx) rows.push({});
        rows[idx][colCode] = val;
        answers[`${code}/${colCode}_${row1Based}`] = val;
      };

      const dropCellsForCode = () => {
        for (const k of Object.keys(answers)) {
          if (k.startsWith(`${code}/`) && /_\d+$/.test(k)) {
            delete answers[k];
          }
        }
      };

      return {
        clear() {
          tables[code] = [];
          dropCellsForCode();
        },
        setRowCount(n) {
          const rows = ensureTable(code);
          if (n < rows.length) {
            for (let i = n; i < rows.length; i++) {
              const r = rows[i];
              for (const col of Object.keys(r)) {
                delete answers[`${code}/${col}_${i + 1}`];
              }
            }
            rows.length = n;
          }
          while (rows.length < n) rows.push({});
        },
        set(colCode, row1Based, val) {
          writeCell(colCode, row1Based, val);
        },
        get(colCode, row1Based) {
          // get() with no args → return whole rows array (used by Orifice
          // and other scripts that scan the table).
          if (colCode === undefined) {
            return tables[code] ?? [];
          }
          const rows = tables[code];
          if (!rows) return undefined;
          const idx = (row1Based ?? 1) - 1;
          const r = rows[idx];
          return r ? (r[colCode] as AnswerValue) : undefined;
        },
        loadContent() {
          return tables[code] ?? [];
        },
      };
    },
    trySet(qCode, val) {
      try {
        answers[`${qCode}/${qCode}`] = val;
      } catch {
        // swallow
      }
    },

    getAnswerLabel(qaPath) {
      const [qCode, aCode] = qaPath.split("/");
      const q = questionsByCode.get(qCode);
      if (!q) return undefined;
      const ans = q._answers.find(
        (a) => a.custrecord_cpqc_answer_code === aCode
      );
      if (!ans) return undefined;
      // Prefer label_html (stripped), fall back to answer.name, then code.
      const labelHtml = ans.custrecord_cpqc_answer_label_html ?? "";
      const stripped = labelHtml
        ? labelHtml.replace(/<[^>]+>/g, "").trim()
        : "";
      return stripped || ans.name || aCode;
    },

    // The answer's raw `name`, which is a different field from its label and is what
    // the Flame Cell scripts build every generated string from. Deliberately does NOT
    // fall back to the label or the code: a script comparing `name === 'Yes'` must not
    // be handed a label that merely looks similar, and a missing answer reading as
    // undefined lets the script's own guard fire (see the Shipping Instructions op,
    // which treats a falsy starbar name as "no instructions").
    getAnswerName(qOrQaPath) {
      const raw = String(qOrQaPath ?? "");
      const slash = raw.indexOf("/");
      const qCode = slash === -1 ? raw : raw.slice(0, slash);
      const q = questionsByCode.get(qCode);
      if (!q) return undefined;
      // "QUESTION" alone means "whatever is selected right now"; the scripts use both
      // forms, sometimes in the same file.
      let aCode = slash === -1 ? "" : raw.slice(slash + 1);
      if (!aCode) {
        const selected = (q._answers ?? []).find(
          (a) => answers[`${qCode}/${a.custrecord_cpqc_answer_code}`] === true
        );
        if (!selected) return undefined;
        aCode = selected.custrecord_cpqc_answer_code;
      }
      const ans = (q._answers ?? []).find(
        (a) => a.custrecord_cpqc_answer_code === aCode
      );
      if (!ans) return undefined;
      return ans.name;
    },

    // Sales-order body fields are not modelled in cpq2: the launch carries only the
    // handful of params on the URL (customer, transaction id, custbody101/102), not
    // arbitrary body fields. Returns "" and NOT undefined on purpose - the Flame Cell
    // Shipping Instructions op concatenates this straight into a string, so undefined
    // would print the literal text "undefined" on a sales order. An empty string makes
    // the script produce no instructions, which is the correct degraded behaviour.
    // Wiring this to the real order means fetching the SO through the proxy at launch.
    getOrderField(fieldId: string) {
      void fieldId;
      return "";
    },

    // Byte-for-byte the body of the declaration in Pilot's own
    // "ID/display/key/description pilot" operation, so the operation that declares it
    // and the one that only calls it behave identically:
    //     function cleanNozzleLabel(label) {
    //         // Remove space and anything inside parentheses
    //         return label.replace(/ *\([^)]*\)/g, '');
    //     }
    // Guarded for a non-string, which the export's version is not: it calls .replace
    // directly and would throw on null, and a throw here loses SALES_DESCRIPTION again.
    cleanNozzleLabel(label: string) {
      return String(label == null ? "" : label).replace(/ *\([^)]*\)/g, "");
    },

    getInputValue(qCode, aCode) {
      if (userFieldHidden(qCode)) return null;
      return answers[`${qCode}/${aCode}`];
    },

    getSelectInternalId(qCode) {
      const aCode = selectedAnswerCode(qCode);
      if (!aCode) return undefined;
      const q = questionsByCode.get(qCode);
      const ans = q?._answers.find(
        (a) => a.custrecord_cpqc_answer_code === aCode
      );
      return ans?.id;
    },

    getTable(code) {
      return api.getQTable(code);
    },

    getQTableCell(tableCode, colCode, row1Based) {
      const rows = tables[tableCode];
      if (!rows) return undefined;
      // If no row specified, default to selected row, then first row.
      const idx =
        row1Based !== undefined
          ? row1Based - 1
          : selectedRows[tableCode] ?? 0;
      const r = rows[idx];
      return r ? r[colCode] : undefined;
    },

    reset(qCode, aCode) {
      answers[`${qCode}/${aCode}`] = null;
    },
    scratchpad,
    getOrderData() {
      // Order/quote-level data not modeled yet.
      return undefined;
    },
    getRuleset(name: string) {
      // Return rule-active subset of the requested ruleset. The script-facing
      // shape mirrors CPQ: each material exposes flat fields like `item`,
      // `rule`, `name`, etc. (mapped from the underlying custrecord names).
      if (name === "materials") {
        return materials
          .filter((m) =>
            evalRule(m.custrecord_cpqc_material_parsed_rule ?? "", answers)
          )
          .map((m) => ({
            id: m.id,
            name: m.name,
            item: m.custrecord_cpqc_material_item,
            rule: m.custrecord_cpqc_material_rule,
            parsed_rule: m.custrecord_cpqc_material_parsed_rule,
            qty: m.custrecord_cpqc_material_item_qty,
            sequence: m.custrecord_cpqc_material_sequence,
            cpq_question: m.custrecord_cpqc_material_item_qa,
          }));
      }
      // Other rulesets (routings, pricing) not modeled yet — return empty so
      // scripts that .forEach over them don't crash.
      return [];
    },
    getLabel() {
      // Generic getLabel — depends on context; return undefined.
      return undefined;
    },
    recalcRulesets() {
      // Stage 5: trigger re-evaluation of materials/routings rulesets.
      return undefined;
    },
    createRecord() {
      // Stage 5: create a NetSuite record (not modeled yet).
      return undefined;
    },
    updateRecordFields() {
      // Stage 5: update fields on a NetSuite record (not modeled yet).
      return undefined;
    },
    getData() {
      // NetSuite item-search stub. Returns a chainable whose .done() is a
      // NO-OP: we don't query NetSuite from the operation runtime. (The
      // configurator talks to NetSuite via the proxy + assemblyBuilder on
      // finalize, not from inside synchronous operation execution.)
      const chain: GetDataChain = {
        done: () => chain,
      };
      return chain;
    },
  };

  return {
    answers,
    tables,
    selectedRows,
    scratchpad,
    setMappingMode: (on: boolean) => {
      mappingMode = on;
    },
    api,
  };
}
